import { createSlice } from "@reduxjs/toolkit";
import {
  AwbStatusesInterface,
  BinStatusesInterface,
  DeliveredAwbHistoryInterface,
  ScheduleWorkingPointsDetailsInterface,
  ScheduleWorkingPointsInterface,
  ScheduleWorkingRoutesDetailsInterface,
  ScheduleWorkingRoutesInterface,
  UndeliveredAwbDataInterface,
  UndeliveredAwbInterface,
  UndeliveredBinsInterface,
  WorkingRoutesNodesInterface,
} from "../interfaces/expedition.interface";

import {
  getScheduleWorkingPoints,
  getScheduleWorkingPointsDetails,
  getWorkingRoutes,
  getWorkingRoutesDetails,
  fetchGetClientsNodes,
  fetchUndeliveredAwb,
  getAwbStatuses,
  fetchUndeliveredBins,
  getBinStatuses,
  fetchDeliveredAwbHistory,
} from "../api/expedition.api";

const initialExpeditionState: {
  scheduleWorkingPoints: ScheduleWorkingPointsInterface;
  scheduleWorkingPointsDetails: ScheduleWorkingPointsDetailsInterface;
  scheduleWorkingRoutes: ScheduleWorkingRoutesInterface;
  scheduleWorkingRoutesDetails: ScheduleWorkingRoutesDetailsInterface;
  workingRoutesNodes: WorkingRoutesNodesInterface;
  undeliveredAwb: UndeliveredAwbInterface;
  deliveredAwbHistory: DeliveredAwbHistoryInterface;
  awbStatuses: AwbStatusesInterface;
  undeliveredBins: UndeliveredBinsInterface;
  binStatuses: BinStatusesInterface;
  clientSelectedUndeliveredAwb: UndeliveredAwbDataInterface | null;
  isChangeDateUndeliveredAwbModalOpen: boolean;
  selectedUndeliveredAwbForChangingDate: UndeliveredAwbDataInterface | null;
} = {
  scheduleWorkingPoints: {
    data: {
      current_page: 1,
      data: [],
      last_page: 1,
      perPage: 1,
      total: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  scheduleWorkingPointsDetails: {
    data: {
      days: [],
      pctLucru: null,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  scheduleWorkingRoutes: {
    data: {
      current_page: 1,
      data: [],
      last_page: 1,
      perPage: 1,
      total: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  scheduleWorkingRoutesDetails: {
    data: {
      days: [],
      ruta: null,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  workingRoutesNodes: {
    data: {
      current_page: 1,
      data: [],
      last_page: 1,
      perPage: 1,
      total: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  undeliveredAwb: {
    data: {
      current_page: 1,
      data: [],
      last_page: 1,
      perPage: 1,
      total: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  deliveredAwbHistory: {
    data: {
      current_page: 1,
      data: [],
      last_page: 1,
      perPage: 1,
      total: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  awbStatuses: {
    data: [],
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  undeliveredBins: {
    data: {
      current_page: 1,
      data: [],
      last_page: 1,
      perPage: 1,
      total: 1,
    },
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  binStatuses: {
    data: [],
    isLoading: false,
    isError: false,
    errorMessage: "",
  },
  clientSelectedUndeliveredAwb: null,
  isChangeDateUndeliveredAwbModalOpen: false,
  selectedUndeliveredAwbForChangingDate: null,
};

const expeditionReducer = createSlice({
  name: "expedition",
  initialState: initialExpeditionState,
  reducers: {
    clearScheduleWorkingPoints: (state) => {
      return {
        ...state,
        scheduleWorkingPoints: {
          ...initialExpeditionState.scheduleWorkingPoints,
        },
      };
    },
    clearScheduleWorkingRoutes: (state) => {
      return {
        ...state,
        scheduleWorkingRoutes: {
          ...initialExpeditionState.scheduleWorkingRoutes,
        },
      };
    },
    clearWorkingRoutesNodes: (state) => {
      return {
        ...state,
        workingRoutesNodes: {
          ...initialExpeditionState.workingRoutesNodes,
        },
      };
    },

    resetScheduleWorkingPointsDetails: (state) => {
      return {
        ...state,
        scheduleWorkingPointsDetails: {
          ...initialExpeditionState.scheduleWorkingPointsDetails,
        },
      };
    },
    resetScheduleWorkingRoutesDetails: (state) => {
      return {
        ...state,
        scheduleWorkingRoutesDetails: {
          ...initialExpeditionState.scheduleWorkingRoutesDetails,
        },
      };
    },
    setClientSelectedUndeliveredAwb: (
      state,
      action: {
        payload: UndeliveredAwbDataInterface | null;
      },
    ) => {
      return {
        ...state,
        clientSelectedUndeliveredAwb: action.payload,
      };
    },
    setSelectedUndeliveredAwbForChangingDate: (
      state,
      action: {
        payload: UndeliveredAwbDataInterface | null;
      },
    ) => {
      return {
        ...state,
        selectedUndeliveredAwbForChangingDate: action.payload,
      };
    },
    openChangeDateUndeliveredAwbModal: (state) => {
      state.isChangeDateUndeliveredAwbModalOpen = true;
    },
    closeChangeDateUndeliveredAwbModal: (state) => {
      state.isChangeDateUndeliveredAwbModalOpen = false;
    },
  },
  extraReducers: (builder) => {
    // get schedule working points start
    builder.addCase(getScheduleWorkingPoints.pending, (state) => {
      return {
        ...state,
        scheduleWorkingPoints: {
          ...initialExpeditionState.scheduleWorkingPoints,
          isLoading: true,
        },
      };
    });
    builder.addCase(getScheduleWorkingPoints.rejected, (state, action: any) => {
      return {
        ...state,
        scheduleWorkingPoints: {
          ...state.scheduleWorkingPoints,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(getScheduleWorkingPoints.fulfilled, (state, action) => {
      return {
        ...state,
        scheduleWorkingPoints: {
          ...state.scheduleWorkingPoints,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // get schedule working points end
    // get schedule working points details start
    // get  schedule working routes details start
    builder.addCase(getScheduleWorkingPointsDetails.pending, (state) => {
      return {
        ...state,
        scheduleWorkingPointsDetails: {
          ...initialExpeditionState.scheduleWorkingPointsDetails,
          isLoading: true,
        },
      };
    });
    builder.addCase(
      getScheduleWorkingPointsDetails.rejected,
      (state, action: any) => {
        return {
          ...state,
          scheduleWorkingPointsDetails: {
            ...state.scheduleWorkingPointsDetails,
            isLoading: false,
            errorMessage: action.payload.message,
            isError: true,
          },
        };
      },
    );
    builder.addCase(
      getScheduleWorkingPointsDetails.fulfilled,
      (state, action) => {
        return {
          ...state,
          scheduleWorkingPointsDetails: {
            ...state.scheduleWorkingPointsDetails,
            isLoading: false,
            data: action.payload,
          },
        };
      },
    );
    // end get schedule working points details end
    // get schedule working routes start
    builder.addCase(getWorkingRoutes.pending, (state) => {
      return {
        ...state,
        scheduleWorkingRoutes: {
          ...initialExpeditionState.scheduleWorkingRoutes,
          isLoading: true,
        },
      };
    });
    builder.addCase(getWorkingRoutes.rejected, (state, action: any) => {
      return {
        ...state,
        scheduleWorkingRoutes: {
          ...state.scheduleWorkingRoutes,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });

    builder.addCase(getWorkingRoutes.fulfilled, (state, action) => {
      return {
        ...state,
        scheduleWorkingRoutes: {
          ...state.scheduleWorkingRoutes,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // get schedule working routes end
    // get  schedule working routes details start
    builder.addCase(getWorkingRoutesDetails.pending, (state) => {
      return {
        ...state,
        scheduleWorkingRoutesDetails: {
          ...initialExpeditionState.scheduleWorkingRoutesDetails,
          isLoading: true,
        },
      };
    });
    builder.addCase(getWorkingRoutesDetails.rejected, (state, action: any) => {
      return {
        ...state,
        scheduleWorkingRoutesDetails: {
          ...state.scheduleWorkingRoutesDetails,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(getWorkingRoutesDetails.fulfilled, (state, action) => {
      return {
        ...state,
        scheduleWorkingRoutesDetails: {
          ...state.scheduleWorkingRoutesDetails,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    //  get  schedule working routes details end
    // get working routes nodes start
    builder.addCase(fetchGetClientsNodes.pending, (state) => {
      return {
        ...state,
        workingRoutesNodes: {
          ...initialExpeditionState.workingRoutesNodes,
          isLoading: true,
        },
      };
    });
    builder.addCase(fetchGetClientsNodes.rejected, (state, action: any) => {
      return {
        ...state,
        workingRoutesNodes: {
          ...state.workingRoutesNodes,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(fetchGetClientsNodes.fulfilled, (state, action) => {
      return {
        ...state,
        workingRoutesNodes: {
          ...state.workingRoutesNodes,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // get working routes nodes end

    // get undelivered awb start
    builder.addCase(fetchUndeliveredAwb.pending, (state) => {
      return {
        ...state,
        undeliveredAwb: {
          ...initialExpeditionState.undeliveredAwb,
          isLoading: true,
        },
      };
    });
    builder.addCase(fetchUndeliveredAwb.rejected, (state, action: any) => {
      return {
        ...state,
        undeliveredAwb: {
          ...state.undeliveredAwb,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(fetchUndeliveredAwb.fulfilled, (state, action) => {
      return {
        ...state,
        undeliveredAwb: {
          ...state.undeliveredAwb,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // get undelivered awb end

    // get delivered awb history start
    builder.addCase(fetchDeliveredAwbHistory.pending, (state) => {
      return {
        ...state,
        deliveredAwbHistory: {
          ...initialExpeditionState.deliveredAwbHistory,
          isLoading: true,
        },
      };
    });
    builder.addCase(fetchDeliveredAwbHistory.rejected, (state, action: any) => {
      return {
        ...state,
        deliveredAwbHistory: {
          ...state.deliveredAwbHistory,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(fetchDeliveredAwbHistory.fulfilled, (state, action) => {
      return {
        ...state,
        deliveredAwbHistory: {
          ...state.deliveredAwbHistory,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // get delivered awb history

    // get awb statuses start
    builder.addCase(getAwbStatuses.pending, (state) => {
      return {
        ...state,
        awbStatuses: {
          ...initialExpeditionState.awbStatuses,
          isLoading: true,
        },
      };
    });
    builder.addCase(getAwbStatuses.rejected, (state, action: any) => {
      return {
        ...state,
        awbStatuses: {
          ...state.awbStatuses,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(getAwbStatuses.fulfilled, (state, action) => {
      return {
        ...state,
        awbStatuses: {
          ...state.awbStatuses,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // get awb statuses end

    // get undelivered bins start
    builder.addCase(fetchUndeliveredBins.pending, (state) => {
      return {
        ...state,
        undeliveredBins: {
          ...initialExpeditionState.undeliveredBins,
          isLoading: true,
        },
      };
    });
    builder.addCase(fetchUndeliveredBins.rejected, (state, action: any) => {
      return {
        ...state,
        undeliveredBins: {
          ...state.undeliveredBins,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(fetchUndeliveredBins.fulfilled, (state, action) => {
      return {
        ...state,
        undeliveredBins: {
          ...state.undeliveredBins,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // get undelivered bins end

    // get bin statuses start
    builder.addCase(getBinStatuses.pending, (state) => {
      return {
        ...state,
        binStatuses: {
          ...initialExpeditionState.binStatuses,
          isLoading: true,
        },
      };
    });
    builder.addCase(getBinStatuses.rejected, (state, action: any) => {
      return {
        ...state,
        binStatuses: {
          ...state.binStatuses,
          isLoading: false,
          errorMessage: action.payload.message,
          isError: true,
        },
      };
    });
    builder.addCase(getBinStatuses.fulfilled, (state, action) => {
      return {
        ...state,
        binStatuses: {
          ...state.binStatuses,
          isLoading: false,
          data: action.payload,
        },
      };
    });
    // get bin statuses end
  },
});

export const {
  clearScheduleWorkingPoints,
  clearScheduleWorkingRoutes,
  clearWorkingRoutesNodes,
  resetScheduleWorkingPointsDetails,
  resetScheduleWorkingRoutesDetails,
  setClientSelectedUndeliveredAwb,
  setSelectedUndeliveredAwbForChangingDate,
  openChangeDateUndeliveredAwbModal,
  closeChangeDateUndeliveredAwbModal,
} = expeditionReducer.actions;

export default expeditionReducer.reducer;
